import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getJob, getJobCandidates, computeJobMatching } from '../services/api';
import ScoreBar from '../components/ScoreBar';

const STAGE_COLORS = {
  new: '#3b82f6', process: '#f59e0b', hired: '#22c55e', refused: '#ef4444', offer: '#8b5cf6',
};
const STAGE_LABELS = {
  new: 'Nouveau', process: 'En process', hired: 'Embauché', refused: 'Refusé', offer: 'Proposition',
};

function GeminiAnalysis({ candidate }) {
  const { gemini_score, gemini_resume, gemini_forces, gemini_faiblesses, gemini_analyse } = candidate;

  if (!gemini_score && !gemini_analyse) return null;

  const scoreColor = gemini_score >= 70 ? '#22c55e' : gemini_score >= 45 ? '#f59e0b' : '#ef4444';

  return (
    <div style={{
      marginTop: 16, padding: 20, borderRadius: 12,
      background: 'linear-gradient(135deg, #f8f7ff 0%, #f0f4ff 100%)',
      border: '1px solid #e0e7ff',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span style={{ fontSize: 20 }}>&#x2728;</span>
        <h4 style={{ margin: 0, color: '#4338ca', fontSize: 15 }}>Analyse Gemini AI</h4>
        <div style={{
          marginLeft: 'auto', background: scoreColor, color: '#fff',
          padding: '4px 14px', borderRadius: 999, fontSize: 14, fontWeight: 700,
        }}>
          {gemini_score}/100
        </div>
      </div>

      {gemini_resume && (
        <p style={{
          fontSize: 14, color: '#1e293b', fontWeight: 600, margin: '0 0 12px',
          padding: '10px 14px', background: '#fff', borderRadius: 8, borderLeft: `4px solid ${scoreColor}`,
        }}>
          {gemini_resume}
        </p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        {gemini_forces && gemini_forces.length > 0 && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#16a34a', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Points forts
            </div>
            {gemini_forces.map((f, i) => (
              <div key={i} style={{
                fontSize: 13, color: '#1e293b', padding: '5px 10px', marginBottom: 4,
                background: '#f0fdf4', borderRadius: 6, borderLeft: '3px solid #22c55e',
              }}>
                {f}
              </div>
            ))}
          </div>
        )}
        {gemini_faiblesses && gemini_faiblesses.length > 0 && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#dc2626', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Points d'attention
            </div>
            {gemini_faiblesses.map((f, i) => (
              <div key={i} style={{
                fontSize: 13, color: '#1e293b', padding: '5px 10px', marginBottom: 4,
                background: '#fef2f2', borderRadius: 6, borderLeft: '3px solid #ef4444',
              }}>
                {f}
              </div>
            ))}
          </div>
        )}
      </div>

      {gemini_analyse && (
        <div style={{
          fontSize: 13, color: '#475569', lineHeight: 1.7,
          padding: '12px 14px', background: '#fff', borderRadius: 8,
          borderLeft: '4px solid #818cf8',
        }}>
          {gemini_analyse}
        </div>
      )}
    </div>
  );
}

function JobDetail() {
  const { reference } = useParams();
  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [computing, setComputing] = useState(false);
  const [message, setMessage] = useState('');
  const [sortBy, setSortBy] = useState('score');
  const [filterStage, setFilterStage] = useState('all');
  const [expandedCandidate, setExpandedCandidate] = useState(null);

  const load = useCallback(async () => {
    try {
      const [jobRes, candRes] = await Promise.all([getJob(reference), getJobCandidates(reference)]);
      setJob(jobRes.data);
      setCandidates(candRes.data);
    } catch { /* ignore */ }
  }, [reference]);

  useEffect(() => { load(); }, [load]);

  const handleMatching = async () => {
    setComputing(true);
    setMessage('');
    try {
      const { data } = await computeJobMatching(reference);
      setMessage(`Matching terminé : ${data.candidates_matched} candidats analysés par Gemini AI`);
      load();
    } catch { setMessage('Erreur lors du matching'); }
    setComputing(false);
  };

  const sorted = [...candidates]
    .filter(c => filterStage === 'all' || c.stage_kind === filterStage)
    .sort((a, b) => {
      if (sortBy === 'score') return (b.final_score || -1) - (a.final_score || -1);
      if (sortBy === 'name') return (a.lastname || '').localeCompare(b.lastname || '');
      if (sortBy === 'date') return (b.created_at || '').localeCompare(a.created_at || '');
      return 0;
    });

  const stageGroups = candidates.reduce((acc, c) => {
    const k = c.stage_kind || 'unknown';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  if (!job) return <div style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>Chargement...</div>;

  return (
    <div style={{ padding: 32, maxWidth: 1200, margin: '0 auto' }}>
      <Link to="/jobs" style={{ color: '#3b82f6', textDecoration: 'none', fontSize: 14 }}>&larr; Retour aux offres</Link>

      <div style={{ ...cardStyle, marginTop: 16, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', margin: 0 }}>{job.name}</h1>
            <p style={{ color: '#64748b', margin: '8px 0 0', fontSize: 14 }}>
              {job.contract_type} &middot; {job.experience_level} &middot; {job.remote}
            </p>
          </div>
          <button onClick={handleMatching} disabled={computing} style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff', border: 'none', borderRadius: 8,
            padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}>
            {computing ? 'Analyse Gemini en cours...' : 'Lancer le Matching AI'}
          </button>
        </div>
        {job.description && (
          <details style={{ marginTop: 16 }}>
            <summary style={{ cursor: 'pointer', color: '#3b82f6', fontSize: 14 }}>Voir la description du poste</summary>
            <div style={{ marginTop: 8, fontSize: 14, color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto' }}>
              {job.description}
            </div>
          </details>
        )}
      </div>

      {message && (
        <div style={{ padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, marginBottom: 16, color: '#166534' }}>
          {message}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 14, color: '#64748b', marginRight: 8 }}>Pipeline :</span>
        <FilterBtn active={filterStage === 'all'} onClick={() => setFilterStage('all')}>
          Tous ({candidates.length})
        </FilterBtn>
        {Object.entries(stageGroups).map(([kind, count]) => (
          <FilterBtn key={kind} active={filterStage === kind} onClick={() => setFilterStage(kind)}
            color={STAGE_COLORS[kind]}>
            {STAGE_LABELS[kind] || kind} ({count})
          </FilterBtn>
        ))}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#94a3b8' }}>Trier par :</span>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{
            padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 13,
          }}>
            <option value="score">Score</option>
            <option value="name">Nom</option>
            <option value="date">Date</option>
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 12 }}>
        {sorted.map(c => (
          <div key={c.reference} style={{
            ...cardStyle,
            border: expandedCandidate === c.reference ? '2px solid #667eea' : '1px solid #e2e8f0',
            cursor: 'pointer',
          }}
          onClick={() => setExpandedCandidate(expandedCandidate === c.reference ? null : c.reference)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                background: STAGE_COLORS[c.stage_kind] || '#94a3b8',
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 16, flexShrink: 0,
              }}>
                {(c.firstname || '?')[0]}{(c.lastname || '?')[0]}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 16 }}>
                  {c.firstname} {c.lastname}
                </div>
                <div style={{ color: '#64748b', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.subtitle || 'Pas de titre'}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' }}>
                  <span style={{
                    fontSize: 11, padding: '2px 8px', borderRadius: 999,
                    background: (STAGE_COLORS[c.stage_kind] || '#94a3b8') + '20',
                    color: STAGE_COLORS[c.stage_kind] || '#94a3b8',
                    fontWeight: 500,
                  }}>
                    {c.stage_name || c.stage_kind || '—'}
                  </span>
                  {c.city && <span style={{ fontSize: 11, color: '#94a3b8' }}>{c.city}</span>}
                  {c.gemini_score > 0 && (
                    <span style={{
                      fontSize: 10, padding: '2px 6px', borderRadius: 999,
                      background: '#eef2ff', color: '#4338ca', fontWeight: 600,
                    }}>
                      AI: {c.gemini_score}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ width: 200, flexShrink: 0 }}>
                {c.final_score !== null ? (
                  <ScoreBar score={c.final_score} label="Match" />
                ) : (
                  <span style={{ fontSize: 13, color: '#94a3b8' }}>Non calculé</span>
                )}
              </div>
            </div>

            {expandedCandidate === c.reference && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  <div>
                    <h4 style={{ color: '#1e293b', marginBottom: 8 }}>Informations</h4>
                    {c.email && <InfoRow label="Email" value={c.email} link={`mailto:${c.email}`} />}
                    {c.phone && <InfoRow label="Téléphone" value={c.phone} link={`tel:${c.phone}`} />}
                    {c.linkedin && <InfoRow label="LinkedIn" value="Voir profil" link={c.linkedin} />}
                    {c.website && <InfoRow label="Site web" value="Voir" link={c.website} />}
                    {c.resume_url && <InfoRow label="CV" value="Télécharger" link={c.resume_url} />}
                    {c.portfolio_url && <InfoRow label="Portfolio" value="Voir" link={c.portfolio_url} />}
                    <InfoRow label="Source" value={c.origin || '—'} />
                    <InfoRow label="Postulé le" value={c.created_at ? c.created_at.slice(0, 10) : '—'} />
                  </div>
                  <div>
                    <h4 style={{ color: '#1e293b', marginBottom: 8 }}>Scores détaillés</h4>
                    {c.final_score !== null ? (
                      <>
                        {c.gemini_score > 0 && <ScoreBar score={c.gemini_score} label="Gemini AI" small />}
                        <ScoreBar score={c.semantic_score} label="Sémantique" small />
                        <ScoreBar score={c.skills_score} label="Compétences" small />
                        <ScoreBar score={c.experience_score} label="Expérience" small />
                        <ScoreBar score={c.education_score} label="Études" small />
                        <ScoreBar score={c.location_score} label="Localisation" small />
                      </>
                    ) : (
                      <p style={{ color: '#94a3b8', fontSize: 13 }}>Lancez le matching pour voir les scores</p>
                    )}
                  </div>
                </div>

                <GeminiAnalysis candidate={c} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function InfoRow({ label, value, link }) {
  return (
    <div style={{ display: 'flex', fontSize: 13, padding: '4px 0', gap: 8 }}>
      <span style={{ color: '#94a3b8', width: 90, flexShrink: 0 }}>{label}</span>
      {link ? (
        <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}
          onClick={e => e.stopPropagation()}>{value}</a>
      ) : (
        <span style={{ color: '#1e293b' }}>{value}</span>
      )}
    </div>
  );
}

function FilterBtn({ children, active, onClick, color = '#3b82f6' }) {
  return (
    <button onClick={onClick} style={{
      background: active ? color : '#fff',
      color: active ? '#fff' : '#64748b',
      border: `1px solid ${active ? color : '#e2e8f0'}`,
      borderRadius: 999, padding: '5px 14px', fontSize: 13,
      cursor: 'pointer', fontWeight: 500, transition: 'all 0.2s',
    }}>
      {children}
    </button>
  );
}

const cardStyle = {
  background: '#fff', borderRadius: 12, padding: 20,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

export default JobDetail;
